import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';

/**
 * 商季 PC 客户端 API 客户端
 *
 * - 基础 URL 使用同源相对路径 /api/v1，由 vite proxy 代理到后端（避免跨域）
 * - 请求拦截器自动注入 Authorization
 * - 响应拦截器统一处理 { code, message, data } 格式
 * - 401 自动跳转登录页
 */

const BASE_URL = '/api/v1';

export const httpClient: AxiosInstance = axios.create({
  baseURL: BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// 请求拦截器：注入 Token
httpClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// 响应拦截器：统一处理响应格式
httpClient.interceptors.response.use(
  (response) => {
    const body = response.data;
    // 统一响应格式 { code, message, data, requestId, timestamp }
    if (body && typeof body.code === 'number') {
      if (body.code === 0) {
        return body.data;
      }
      // 业务错误
      const err = new Error(body.message || '请求失败') as Error & {
        code: number;
        requestId?: string;
      };
      err.code = body.code;
      err.requestId = body.requestId;
      return Promise.reject(err);
    }
    // 非统一格式（如文件下载），直接返回
    return body;
  },
  (error) => {
    if (error.response) {
      const { status, data } = error.response;
      if (status === 401) {
        // Token 失效，清除并跳登录
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        if (window.location.hash !== '#/login') {
          window.location.hash = '#/login';
        }
      }
      const body = data || {};
      const err = new Error(body.message || `请求失败 (${status})`) as Error & {
        code: number;
        status: number;
      };
      err.code = body.code || status;
      err.status = status;
      return Promise.reject(err);
    }
    return Promise.reject(error);
  },
);

/**
 * 通用请求方法
 */
export async function request<T = unknown>(config: AxiosRequestConfig): Promise<T> {
  return httpClient.request<T, T>(config);
}

export default httpClient;
